import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightAddon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  Switch,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  AcceptedInquiryAlignment,
  Offer,
  OfferGroupBranchCurrency,
  offerApi,
} from '@texas/api/endpoints/offerApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { Icons } from '@texas/components/shared/Icons';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import {
  exchangeRateCalculation,
  marginBy,
  round,
} from '@texas/utils/helpers/numberHelpers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { evaluateProfit, profitTextColor } from '../sales/shared';
import {
  SelectedOfferTierPrice,
  useOfferedOfferSelection,
} from './OfferedOfferSelectionContext';
import { useOffer } from './OfferContext';
import { SetOfferPricesModal } from './SetOfferPricesModal';
import { TierPriceHistoryModal } from './TierPriceHistoryModal';

interface OfferPriceTableProps {
  currencies: OfferGroupBranchCurrency;
  matchedInquiries: AcceptedInquiryAlignment[];
  findCurrency: (id: number) => { code: string } | undefined;
  onPricesUpdated: () => void;
}

export function OfferPriceTable({
  currencies,
  matchedInquiries,
  findCurrency,
  onPricesUpdated,
}: OfferPriceTableProps) {
  const { t } = useTranslation();
  const isDark = useColorModeValue(false, true);
  const { isMatchedInquiryPriceVisible, toggleMatchedInquiryPrice } =
    useOffer();
  const { request: updateTier } = useApiRequest(offerApi.updateTierPrice);
  const { request: bulkUpdate, loading: bulkUpdating } = useApiRequest(
    offerApi.bulkUpdateTierPrices,
  );
  const { request: unapproveOffers, loading: unapproving } = useApiRequest(
    offerApi.unapproveOffers,
  );
  const { request: createTierPriceVersion } = useApiRequest(
    offerApi.createTierPriceVersion,
  );
  const {
    selectedTierPriceIds,
    selectedCount,
    selectedTierPrices,
    unapprovedSelectedTierPrices,
    approvedSelectedTierPrices,
    clearSelection,
    registerTierPrices,
    toggleTierPrice,
    selectTierPrices,
    clearTierPrices,
    toggleTierPrices,
    openMakeOffer,
  } = useOfferedOfferSelection();

  const [setPriceOpen, setSetPriceOpen] = useState(false);
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const [comparePreviousPrice, setComparePreviousPrice] = useState(false);
  const [bulkPrice, setBulkPrice] = useState<string>('');
  const [setPriceMode, setSetPriceMode] = useState<'fixed' | 'percentage'>(
    'fixed',
  );
  const [historyTierPriceId, setHistoryTierPriceId] = useState<number | null>(
    null,
  );
  const {
    value: acceptPriceIds,
    isOpen: isAcceptPriceOpen,
    onOpen: openAcceptPrice,
    onClose: closeAcceptPrice,
  } = useValueDisclosure<number[]>();
  const {
    isOpen: isHistoryOpen,
    onOpen: openHistory,
    onClose: closeHistory,
  } = useDisclosure();

  const allOffers = getCurrencyOffers(currencies);

  const quantities = [
    ...new Set(allOffers.flatMap((o) => o.tierPrices.map((tp) => tp.quantity))),
  ].sort((a, b) => a - b);

  const sortedOffers = [...allOffers].sort(
    (a, b) => rankOffer(a) - rankOffer(b),
  );
  const visibleMatchedInquiries = useMemo(
    () =>
      matchedInquiries.filter((inquiry) =>
        isMatchedInquiryPriceVisible(inquiry.inquiryId),
      ),
    [isMatchedInquiryPriceVisible, matchedInquiries],
  );
  const currencyCode = findCurrency(currencies.currencyId)?.code ?? '';
  const convertInquiryPrice = useCallback(
    (price: number, inquiryExchangeRate: number, targetExchangeRate: number) =>
      exchangeRateCalculation(
        price,
        inquiryExchangeRate,
        targetExchangeRate,
        5,
      ),
    [],
  );

  const rowLabel = useCallback(
    (o: Offer) => {
      if (!o.matrixNodeValueXName && !o.matrixNodeValueYName)
        return t('offer.offer');
      if (o.matrixNodeValueXName && o.matrixNodeValueYName)
        return `${o.matrixNodeValueXName} / ${o.matrixNodeValueYName}`;
      return o.matrixNodeValueXName ?? o.matrixNodeValueYName ?? '-';
    },
    [t],
  );
  const tierPriceDetails = useMemo(
    () =>
      sortedOffers.flatMap((offer) =>
        offer.tierPrices.map((tp) => ({
          id: tp.id,
          quantity: tp.quantity,
          price: tp.price,
          acceptedDate: tp.acceptedDate,
          isInOfferedOffer: tp.isInOfferedOffer,
          rowLabel: rowLabel(offer),
          currencyCode,
          exchangeRate: offer.exchangeRate,
          inquiryPrices: visibleMatchedInquiries.map((inquiry) => {
            const matchedTierPrice = getAlignedInquiryTierPrice(
              inquiry,
              offer,
              tp.quantity,
            );
            return {
              inquiryId: inquiry.inquiryId,
              inquiryNumber: inquiry.inquiryNumber,
              price:
                matchedTierPrice?.price != null
                  ? convertInquiryPrice(
                      matchedTierPrice.price,
                      inquiry.exchangeRate,
                      offer.exchangeRate,
                    )
                  : null,
            };
          }),
        })),
      ),
    [
      convertInquiryPrice,
      currencyCode,
      rowLabel,
      sortedOffers,
      visibleMatchedInquiries,
    ],
  );
  useEffect(() => {
    registerTierPrices(tierPriceDetails);
  }, [registerTierPrices, tierPriceDetails]);

  const editableUnapprovedSelectedTierPrices =
    unapprovedSelectedTierPrices.filter((tp) => !tp.isInOfferedOffer);
  const editableUnapprovedSelectedIds =
    editableUnapprovedSelectedTierPrices.map((tp) => tp.id);
  const acceptableUnapprovedSelectedIds = unapprovedSelectedTierPrices
    .filter(
      (tp) => tp.price != null && tp.price > 0 && tp.isInOfferedOffer != null,
    )
    .map((tp) => tp.id);
  const approvedSelectedIds = approvedSelectedTierPrices.map((tp) => tp.id);
  const newVersionSelectedIds = selectedTierPrices
    .filter((tp) => tp.acceptedDate != null || tp.isInOfferedOffer != null)
    .map((tp) => tp.id);
  const makeOfferSelectedCount = selectedTierPrices.filter(
    (tp) => tp.price != null && tp.price > 0,
  ).length;
  const bulkNumericValue =
    bulkPrice === '' || Number.isNaN(parseFloat(bulkPrice))
      ? null
      : parseFloat(bulkPrice);
  const getPrimaryInquiryPrice = (tp: SelectedOfferTierPrice) =>
    tp.inquiryPrices.find((price) => price.price != null)?.price ?? null;
  const calculateBulkPrice = (tp: SelectedOfferTierPrice) => {
    if (bulkNumericValue == null) return null;
    if (setPriceMode === 'fixed') return bulkNumericValue;

    const base = getPrimaryInquiryPrice(tp);
    if (base == null || base <= 0) return null;

    return round(base * (1 + bulkNumericValue / 100), 5);
  };
  const tableTierPriceIds = useMemo(
    () => sortedOffers.flatMap((offer) => offer.tierPrices.map((tp) => tp.id)),
    [sortedOffers],
  );

  if (quantities.length === 0) return null;

  const selectRow = (ids: number[]) => {
    toggleTierPrices(ids);
  };

  const selectColumn = (qty: number) => {
    const ids = sortedOffers.flatMap((o) =>
      o.tierPrices.filter((tp) => tp.quantity === qty).map((tp) => tp.id),
    );
    toggleTierPrices(ids);
  };
  const isWholeTableSelected =
    tableTierPriceIds.length > 0 &&
    tableTierPriceIds.every((id) => selectedTierPriceIds.has(id));

  const toggleTableSelection = () => {
    if (isWholeTableSelected) {
      clearTierPrices(tableTierPriceIds);
      return;
    }
    selectTierPrices(tableTierPriceIds);
  };

  return (
    <>
      {matchedInquiries.length > 0 && (
        <Flex justify="flex-end" mb={2} gap={3} wrap="wrap">
          <Text fontSize="sm" color="gray.500">
            {t('offer.showPrices')}
          </Text>
          {matchedInquiries.map((inquiry) => (
            <Checkbox
              key={inquiry.inquiryId}
              size="sm"
              isChecked={isMatchedInquiryPriceVisible(inquiry.inquiryId)}
              onChange={() => toggleMatchedInquiryPrice(inquiry.inquiryId)}
            >
              {inquiry.inquiryNumber}
            </Checkbox>
          ))}
        </Flex>
      )}
      <Flex justify="flex-end" mb={2} gap={3} wrap="wrap">
        <FormControl display="flex" alignItems="center" gap={2} w="auto">
          <Switch
            id={`compare-previous-offer-price-${currencies.currencyId}`}
            size="sm"
            isChecked={comparePreviousPrice}
            onChange={(e) => setComparePreviousPrice(e.target.checked)}
          />
          <FormLabel
            htmlFor={`compare-previous-offer-price-${currencies.currencyId}`}
            mb={0}
            fontSize="sm"
            fontWeight="normal"
            cursor="pointer"
          >
            {t('general.comparePreviousPrice')}
          </FormLabel>
        </FormControl>
        <Button
          size="xs"
          variant="texas-light"
          leftIcon={<Icons.CheckAll boxSize={3} />}
          isDisabled={tableTierPriceIds.length === 0}
          onClick={toggleTableSelection}
        >
          {isWholeTableSelected
            ? t('general.deselectAll')
            : t('general.selectAll')}
        </Button>
      </Flex>
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th />
              {quantities.map((qty) => (
                <Th
                  key={qty}
                  cursor="pointer"
                  onClick={() => selectColumn(qty)}
                >
                  <Flex justify="flex-start" align="center" gap={1}>
                    <Tooltip label={t('inquiry.selectColumn')}>
                      <IconButton
                        aria-label={t('inquiry.selectColumn')}
                        icon={<Icons.CheckAll boxSize={3} />}
                        size="xs"
                        variant="ghost"
                        minW={5}
                        h={5}
                        onClick={(e) => {
                          e.stopPropagation();
                          selectColumn(qty);
                        }}
                      />
                    </Tooltip>
                    {qty}
                  </Flex>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {sortedOffers.map((offer) => (
              <Tr key={offer.id}>
                <Td
                  fontWeight={rankOffer(offer) === 0 ? 'semibold' : 'normal'}
                  cursor="pointer"
                  onClick={() => selectRow(offer.tierPrices.map((tp) => tp.id))}
                >
                  <Flex align="center" gap={1}>
                    <Tooltip label={t('inquiry.selectRow')}>
                      <IconButton
                        aria-label={t('inquiry.selectRow')}
                        icon={<Icons.CheckAll boxSize={3} />}
                        size="xs"
                        variant="ghost"
                        minW={5}
                        h={5}
                        onClick={(e) => {
                          e.stopPropagation();
                          selectRow(offer.tierPrices.map((tp) => tp.id));
                        }}
                      />
                    </Tooltip>
                    <Text as="span">{rowLabel(offer)}</Text>
                  </Flex>
                </Td>
                {quantities.map((qty) => {
                  const tp = offer.tierPrices.find((t) => t.quantity === qty);
                  const isAccepted = !!tp?.acceptedDate;
                  const isInOfferedOffer = !!tp?.isInOfferedOffer;
                  const isLocked = isAccepted || isInOfferedOffer;
                  const hasCellActions =
                    isAccepted || isInOfferedOffer || !!tp?.basedOnId;
                  const hasPreviousPriceComparison =
                    comparePreviousPrice &&
                    tp?.price != null &&
                    tp.basedOnPrice != null;
                  const hasPriceMetadata =
                    hasPreviousPriceComparison ||
                    (tp != null && isAccepted && !!tp.acceptedByUserName) ||
                    isInOfferedOffer;

                  return (
                    <Td
                      key={qty}
                      transition="background-color 0.2s"
                      bg={
                        tp && selectedTierPriceIds.has(tp.id)
                          ? 'blue.800'
                          : isAccepted
                          ? 'green.900'
                          : isInOfferedOffer
                          ? 'yellow.900'
                          : undefined
                      }
                      _light={{
                        bg:
                          tp && selectedTierPriceIds.has(tp.id)
                            ? 'blue.100'
                            : isAccepted
                            ? 'green.50'
                            : isInOfferedOffer
                            ? 'yellow.100'
                            : undefined,
                      }}
                    >
                      {tp ? (
                        <Flex direction="column" gap={0}>
                          <Flex align="flex-start" gap={2}>
                            <InputGroup size="xs" w="auto" flexShrink={0}>
                              <Input
                                type="number"
                                min={0.01}
                                step="any"
                                defaultValue={tp.price ?? ''}
                                placeholder="-"
                                key={`${tp.id}-${tp.price}`}
                                isDisabled={isLocked}
                                onBlur={async (e) => {
                                  if (isLocked) return;
                                  const val = e.target.value;
                                  const parsedValue = parseFloat(val);
                                  if (
                                    val === '' ||
                                    Number.isNaN(parsedValue) ||
                                    parsedValue <= 0
                                  ) {
                                    e.target.value = tp.price?.toString() ?? '';
                                    return;
                                  }
                                  const response = await updateTier(
                                    tp.id,
                                    parsedValue,
                                  );
                                  if (!response.hasError) {
                                    onPricesUpdated();
                                  }
                                }}
                                w="80px"
                              />
                              <InputRightAddon>{currencyCode}</InputRightAddon>
                            </InputGroup>
                            {hasPriceMetadata && (
                              <Flex direction="column">
                                {hasPreviousPriceComparison && (
                                  <PreviousPriceComparison
                                    currentPrice={tp.price}
                                    previousPrice={tp.basedOnPrice}
                                    currencyCode={currencyCode}
                                  />
                                )}
                                {isAccepted && tp.acceptedByUserName && (
                                  <Tooltip
                                    label={
                                      tp.acceptedDate
                                        ? formatDate(Locale.En, tp.acceptedDate)
                                        : undefined
                                    }
                                    placement="top"
                                  >
                                    <Text
                                      fontSize="2xs"
                                      color="green.400"
                                      display="inline-block"
                                      whiteSpace="nowrap"
                                    >
                                      {tp.acceptedByUserName}
                                    </Text>
                                  </Tooltip>
                                )}
                                {isInOfferedOffer && (
                                  <Text
                                    fontSize="2xs"
                                    color="yellow.300"
                                    whiteSpace="nowrap"
                                    _light={{ color: 'yellow.700' }}
                                  >
                                    {t('offer.inOfferedOffer', {
                                      offerNumber: tp.isInOfferedOffer,
                                    })}
                                  </Text>
                                )}
                              </Flex>
                            )}
                            <Checkbox
                              ml="auto"
                              mt={1}
                              isChecked={selectedTierPriceIds.has(tp.id)}
                              onChange={() => toggleTierPrice(tp.id)}
                            />
                            {hasCellActions && (
                              <Menu>
                                <MenuButton
                                  as={IconButton}
                                  aria-label={t('offer.cellActions')}
                                  icon={<Icons.DotsHorizontal boxSize={3} />}
                                  size="xs"
                                  variant="ghost"
                                />
                                <MenuList>
                                  {(isAccepted || isInOfferedOffer) && (
                                    <MenuItem
                                      onClick={async () => {
                                        const res =
                                          await createTierPriceVersion(tp.id);
                                        if (!res.hasError) onPricesUpdated();
                                      }}
                                    >
                                      {t('offer.newVersion')}
                                    </MenuItem>
                                  )}
                                  {tp.basedOnId && (
                                    <MenuItem
                                      onClick={() => {
                                        setHistoryTierPriceId(tp.id);
                                        openHistory();
                                      }}
                                    >
                                      {t('offer.openHistory')}
                                    </MenuItem>
                                  )}
                                </MenuList>
                              </Menu>
                            )}
                          </Flex>
                          {visibleMatchedInquiries.map((inquiry) => {
                            const itp = getAlignedInquiryTierPrice(
                              inquiry,
                              offer,
                              qty,
                            );
                            if (!itp) return null;
                            const inquiryPrice =
                              itp.price != null
                                ? convertInquiryPrice(
                                    itp.price,
                                    inquiry.exchangeRate,
                                    offer.exchangeRate,
                                  )
                                : null;
                            if (inquiryPrice == null) return null;
                            const margin =
                              tp.price != null
                                ? marginBy(tp.price, inquiryPrice, false)
                                : null;
                            const profit =
                              tp.price != null
                                ? evaluateProfit(tp.price, inquiryPrice)
                                : null;
                            return (
                              <Text
                                key={inquiry.inquiryId}
                                fontSize="2xs"
                                color={
                                  profit == null
                                    ? 'gray.500'
                                    : profitTextColor(profit, isDark)
                                }
                              >
                                {[
                                  `${inquiryPrice} ${currencyCode}`,
                                  inquiry.inquiryNumber,
                                  margin != null
                                    ? `${margin.toFixed(1)}%`
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </Text>
                            );
                          })}
                        </Flex>
                      ) : (
                        <Text fontSize="xs" color="gray.500">
                          -
                        </Text>
                      )}
                    </Td>
                  );
                })}
              </Tr>
            ))}
            {currencies.missingCells.map((cell) => (
              <Tr key={`missing-${cell.xId}-${cell.yId}`} opacity={0.4}>
                <Td fontStyle="italic" color="gray.400">
                  {cell.xName} / {cell.yName}
                </Td>
                {quantities.map((qty) => (
                  <Td key={qty} />
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      {selectedCount > 0 && (
        <Flex align="center" mt={2} gap={2} flexWrap="wrap">
          <Button size="xs" variant="link" onClick={clearSelection}>
            {t('offer.deselectAllCells')}
          </Button>
          <Button
            size="xs"
            variant="texas-light"
            isDisabled={editableUnapprovedSelectedIds.length === 0}
            onClick={() => setSetPriceOpen(true)}
          >
            {t('offer.setPriceForCells', {
              count: editableUnapprovedSelectedIds.length,
            })}
          </Button>
          <Button
            size="xs"
            variant="texas-light"
            leftIcon={<Icons.Send />}
            isDisabled={makeOfferSelectedCount === 0}
            onClick={openMakeOffer}
          >
            {t('offer.makeOffer')} ({makeOfferSelectedCount})
          </Button>
          <Button
            size="xs"
            variant="texas-light"
            leftIcon={<Icons.Checkmark />}
            isDisabled={acceptableUnapprovedSelectedIds.length === 0}
            onClick={() => openAcceptPrice(acceptableUnapprovedSelectedIds)}
          >
            {t('offer.acceptPrice')} ({acceptableUnapprovedSelectedIds.length})
          </Button>
          <Button
            size="xs"
            variant="texas-light"
            isLoading={unapproving}
            isDisabled={approvedSelectedIds.length === 0}
            onClick={async () => {
              const res = await unapproveOffers(approvedSelectedIds);
              if (!res.hasError) {
                clearSelection();
                onPricesUpdated();
              }
            }}
          >
            {t('offer.unapprovePrices', {
              count: approvedSelectedIds.length,
            })}
          </Button>
          <Button
            size="xs"
            variant="texas-light"
            isDisabled={newVersionSelectedIds.length === 0}
            onClick={() => setNewVersionOpen(true)}
          >
            {t('offer.createNewVersion')} ({newVersionSelectedIds.length})
          </Button>
        </Flex>
      )}

      <SetOfferPricesModal
        isOpen={setPriceOpen}
        isDark={isDark}
        tierPrices={editableUnapprovedSelectedTierPrices}
        mode={setPriceMode}
        setMode={setSetPriceMode}
        bulkPrice={bulkPrice}
        setBulkPrice={setBulkPrice}
        loading={bulkUpdating}
        onClose={() => setSetPriceOpen(false)}
        getPrimaryInquiryPrice={getPrimaryInquiryPrice}
        calculateBulkPrice={calculateBulkPrice}
        onApply={async () => {
          if (setPriceMode === 'fixed') {
            const res = await bulkUpdate({
              tierPriceIds: editableUnapprovedSelectedIds,
              price: bulkNumericValue,
            });
            if (res.hasError) return;
          } else {
            for (const tierPrice of editableUnapprovedSelectedTierPrices) {
              const newPrice = calculateBulkPrice(tierPrice);
              const res = await updateTier(tierPrice.id, newPrice);
              if (res.hasError) return;
            }
          }

          setSetPriceOpen(false);
          clearSelection();
          setBulkPrice('');
          onPricesUpdated();
        }}
      />

      <Modal
        isOpen={newVersionOpen}
        onClose={() => setNewVersionOpen(false)}
        size="sm"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('offer.createNewVersions')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm">
              {t('offer.createNewVersionsConfirm', {
                count: newVersionSelectedIds.length,
              })}
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setNewVersionOpen(false)}
            >
              {t('general.cancel')}
            </Button>
            <Button
              variant="texas-solid"
              size="sm"
              onClick={async () => {
                for (const id of newVersionSelectedIds) {
                  await createTierPriceVersion(id);
                }
                setNewVersionOpen(false);
                clearSelection();
                onPricesUpdated();
              }}
            >
              {t('general.confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {acceptPriceIds && (
        <VerifyDialogWithRequest
          key={acceptPriceIds.join(',')}
          headerTitle={t('offer.acceptPrice')}
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('offer.acceptPrice')}
          primaryRequest={offerApi.approveOffers}
          args={[acceptPriceIds]}
          isOpen={isAcceptPriceOpen}
          onClose={closeAcceptPrice}
          onPerformed={() => {
            clearSelection();
            onPricesUpdated();
          }}
          onSuccessTitle={t('offer.acceptedPrices')}
        >
          {t('offer.acceptPricesConfirm', { count: acceptPriceIds.length })}
        </VerifyDialogWithRequest>
      )}

      <TierPriceHistoryModal
        tierPriceId={historyTierPriceId}
        currencyCode={currencyCode}
        isOpen={isHistoryOpen}
        onClose={closeHistory}
      />
    </>
  );
}

function getCurrencyOffers(currencies: OfferGroupBranchCurrency) {
  return currencies.childOffers.length > 0
    ? currencies.childOffers
    : currencies.masterOffer
    ? [currencies.masterOffer]
    : [];
}

function getAlignedInquiryTierPrice(
  inquiry: AcceptedInquiryAlignment,
  offer: Offer,
  quantity: number,
) {
  return inquiry.tierPrices.find(
    (tierPrice) =>
      tierPrice.quantity === quantity &&
      tierPrice.matrixNodeValueXId === offer.matrixNodeValueXId &&
      tierPrice.matrixNodeValueYId === offer.matrixNodeValueYId,
  );
}

function rankOffer(o: Offer) {
  if (!o.matrixNodeValueXId && !o.matrixNodeValueYId) return 0;
  if (o.matrixNodeValueYId && !o.matrixNodeValueXId) return 1;
  if (o.matrixNodeValueXId && !o.matrixNodeValueYId) return 2;
  return 3;
}

function PreviousPriceComparison({
  currentPrice,
  previousPrice,
  currencyCode,
}: {
  currentPrice: number | null;
  previousPrice: number | null;
  currencyCode: string;
}) {
  const { t } = useTranslation();
  if (currentPrice == null || previousPrice == null) return null;

  const isBetter = currentPrice > previousPrice;
  const isSame = currentPrice === previousPrice;
  const Icon = isSame
    ? Icons.ArrowRight
    : isBetter
    ? Icons.ChevronUp
    : Icons.ChevronDown;
  const color = isSame ? 'gray.500' : isBetter ? 'green.400' : 'red.400';

  return (
    <Flex align="center" gap={1} color={color}>
      <Icon boxSize={3} />
      <Text fontSize="2xs" whiteSpace="nowrap">
        {t('general.previousPrice')}: {previousPrice} {currencyCode}
      </Text>
    </Flex>
  );
}
